import { action } from "typesafe-actions";
import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { api } from "../../api/api";
import { IEvent } from "./event.types";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";

// Action Types
export const FETCH_EVENT_LIST_PROGRESS = "FETCH_EVENT_LIST_PROGRESS";
export const FETCH_EVENT_LIST_SUCCESS = "FETCH_EVENT_LIST_SUCCESS";
export const FETCH_EVENT_LIST_FAILED = "FETCH_EVENT_LIST_FAILED";

export const FETCH_EVENT_PROGRESS = "FETCH_EVENT_PROGRESS";
export const FETCH_EVENT_SUCCESS = "FETCH_EVENT_SUCCESS";
export const FETCH_EVENT_FAILED = "FETCH_EVENT_FAILED";

// Action Creators
export const fetchEventListProgress = () => action(FETCH_EVENT_LIST_PROGRESS);
export const fetchEventListSuccess = (data: IEvent[], totalRecords: number) =>
  action(FETCH_EVENT_LIST_SUCCESS, { data, totalRecords });
export const fetchEventListFailed = () => action(FETCH_EVENT_LIST_FAILED);

export const fetchEventProgress = () => action(FETCH_EVENT_PROGRESS);
export const fetchEventSuccess = (data: IEvent) =>
  action(FETCH_EVENT_SUCCESS, { data });
export const fetchEventFailed = () => action(FETCH_EVENT_FAILED);

// Async Actions
export const fetchEventListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    status: string,
    company_outlet_uuid: string,
    date?: IDataTableV2DateState["dates"],
    searchValue?: IDatatableV2AdvancedSearchFilter,
    searchByName?: string,
    companyFilter?: string | null
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      let searchQuery = "";

      if (searchByName) {
        searchQuery += `&event_name=${searchByName}`;
      }

      if ((searchValue?.length ?? 0) > 0 || companyFilter) {
        const advanceFilter = [...(searchValue || [])];

        if (companyFilter) {
          advanceFilter.push({
            column: ["company_outlet_uuid"],
            value: companyFilter,
            logicalOperator: "AND",
            operator: "EQUALS",
          });
        }

        searchQuery += `&advanceFilter=${JSON.stringify(advanceFilter)}`;
      }

      if (date) {
        searchQuery += `&from_date=${date.fromDate}&to_date=${date.toDate}`;
      }

      if (status && status !== "-1") {
        searchQuery += `&status=${status}`;
      }

      const finalUrl =
        `/event/get-event?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&company_outlet_uuid=${company_outlet_uuid}` +
        searchQuery;

      dispatch(fetchEventListProgress());
      dispatch(saveLoaderProgress());
      const res = await api.get(finalUrl);
      const data: IEvent[] = res.data.data;
      const totalRecords = res.data.totalRecords;

      dispatch(fetchEventListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchEventListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err?.response?.data?.message || "Failed to fetch events",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const fetchEventAsync =
  (eventUuid: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchEventProgress());
      dispatch(saveLoaderProgress());
      const res = await api.get(`/event/get-event?event_uuid=${eventUuid}`);
      const data = res.data.data;
      if (data.length > 0) {
        dispatch(fetchEventSuccess(data[0]));
      } else {
        dispatch(fetchEventFailed());
      }
    } catch (err: any) {
      dispatch(fetchEventFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err?.response?.data?.message || "Failed to fetch event",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const upsertEventAsync =
  (
    event: IEvent,
    onCallback: (isSuccess?: boolean, data?: IEvent) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(saveLoaderProgress());
    try {
      // Ensure company_outlet_uuid is present
      if (!event.company_outlet_uuid) {
        throw new Error("company_outlet_uuid is required");
      }

      const {
        create_ts,
        insert_ts,
        created_by_uuid,
        created_by_name,
        modified_by_uuid,
        modified_by_name,
        ...rest
      } = event;

      const res = await api.post("/event/upsert-event", rest);
      dispatch(
        showMessage({
          type: "success",
          message: res.data.message || "Event saved successfully",
          displayAs: "snackbar",
        })
      );
      const data = res.data.data;
      onCallback(true, data);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err?.response?.data?.message || "Failed to save event",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const CLEAR_EVENT_STATE = "CLEAR_EVENT_STATE";
export const CLEAR_EVENT = "CLEAR_EVENT";

export const clearEventState = () => action(CLEAR_EVENT_STATE);
export const clearEvent = () => action(CLEAR_EVENT);
